import { SMSTag } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/models.pb';
import { SchemaIO } from '@frontend/schema';
import { useSMSDataV4QueryUpdaters } from '../../v4/query-updaters';
import { SMSMutationContext, useSMSDataV3QueryUpdaters } from '../query-updaters';
import { ServiceMutations } from '../types';
import { UseSMSDataV3MutationEndpointArgs, useSMSDataV3Mutation } from './use-sms-data-v3-mutation';

type MutationContext<C = unknown> = {
  mutationContext: SMSMutationContext & { didOptimisticUpdate: boolean };
  otherContext?: C;
};

/**
 * A hook that returns a mutation for the `DismissTag` mutation endpoint.
 * It handles query invalidation for the relevant query endpoints internally.
 * @param options (optional) The options to pass to `useMutation`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param optimisticUpdate (optional) Whether to perform an optimistic update. Defaults to `true`.
 */
export const useDismissTagMutation = <
  E = unknown,
  C = unknown,
  OtherOptions extends object = never,
  RequestOverride extends SchemaIO<ServiceMutations['DismissTag']>['input'] = SchemaIO<
    ServiceMutations['DismissTag']
  >['input']
>({
  options,
  optimisticUpdate = true,
  ...args
}: UseSMSDataV3MutationEndpointArgs<'DismissTag', E, C | undefined, OtherOptions, RequestOverride> & {
  optimisticUpdate?: boolean;
} = {}) => {
  const { updateSMS } = useSMSDataV3QueryUpdaters();
  const v4QueryUpdaters = useSMSDataV4QueryUpdaters({ httpOptions: args.httpOptions });

  return useSMSDataV3Mutation<'DismissTag', E, MutationContext<C>, OtherOptions, RequestOverride>({
    endpointName: 'DismissTag',
    ...args,
    options: {
      ...options,
      onMutate: async (request) => {
        const mutationContext: MutationContext['mutationContext'] = {
          didOptimisticUpdate: false,
        };

        if (optimisticUpdate) {
          // Use V3 SMS Data
          const newContext = updateSMS({
            matchValues: { id: request.smsId, locationId: request.groupId, threadId: request.threadId },
            newValues: (sms) => ({
              ...sms,
              tags: sms.tags.filter((tagId) => tagId !== request.tagId),
            }),
            skipInvalidation: true,
          });

          if (newContext.originalSMS || newContext.updatedSMS) {
            mutationContext.didOptimisticUpdate = true;
          }
          if (!mutationContext.originalSMS) mutationContext.originalSMS = newContext.originalSMS;
          if (!mutationContext.updatedSMS) mutationContext.updatedSMS = newContext.updatedSMS;
        }

        return {
          mutationContext,
          otherContext: await options?.onMutate?.(request),
        };
      },
      onSuccess: async (response, request, context) => {
        const newThreadValues = {
          tagId: request.tagId,
          isDismissed: true,
          tagInfo: response.nextSmsWTag,
        };

        if (!context.mutationContext.didOptimisticUpdate) {
          // Use V4 and V3 query updaters
          updateSMS({
            matchValues: { id: request.smsId, locationId: request.groupId, threadId: request.threadId },
            newValues: (sms) => ({
              ...sms,
              tags: sms.tags.filter((tagId) => tagId !== request.tagId),
            }),
            newThreadValues,
            skipInvalidation: true,
          });

          v4QueryUpdaters.upsertThread({
            matchValues: {
              threadId: request.threadId,
              locationId: request.groupId,
            },
            updater: (thread) => {
              if (!thread) return undefined;

              if (response.nextSmsWTag.smsId) {
                const { newTags } = thread.uniqueTags?.reduce<{ newTags: SMSTag[]; hasUpdatedTag: boolean }>(
                  (acc, tag, index) => {
                    if (request.tagId === tag.tagId) {
                      acc.hasUpdatedTag = true;
                      acc.newTags.push(response.nextSmsWTag);
                    }

                    const isLastTag = thread.uniqueTags?.length && index === thread.uniqueTags.length - 1;

                    if (isLastTag && !acc.hasUpdatedTag) {
                      acc.newTags.push(response.nextSmsWTag);
                    }

                    return acc;
                  },
                  { newTags: [], hasUpdatedTag: false }
                ) ?? { newTags: [response.nextSmsWTag] };

                return {
                  ...thread,
                  uniqueTags: newTags,
                };
              } else {
                return {
                  ...thread,
                  uniqueTags: thread.uniqueTags?.filter((tag) => tag.tagId !== request.tagId),
                };
              }
            },
            isOptimistic: false,
          });
        }

        return options?.onSuccess?.(response, request, context?.otherContext);
      },
      onError: (error, request, context) => {
        if (context?.mutationContext.didOptimisticUpdate) {
          updateSMS({
            matchValues: { id: request.smsId, locationId: request.groupId, threadId: request.threadId },
            newValues:
              context.mutationContext.originalSMS ??
              ((sms) => ({
                ...sms,
                tags: [...sms.tags, request.tagId],
              })),
          });
          context.mutationContext.revertOptimisticUpdate?.();
        }

        return options?.onError?.(error, request, context?.otherContext);
      },
      onSettled: (response, error, request, context) => {
        return options?.onSettled?.(response, error, request, context?.otherContext);
      },
    },
  });
};
